import { RISK_STATUS_LABELS } from '@/pages/risk/RiskBadges'
import { useEffect, useRef, useState } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { AssessRiskDialog } from '@/pages/risk/AssessRiskDialog'
import { RiskAssessmentDocumentDialog } from '@/pages/risk/RiskAssessmentDocumentDialog'
import { TaskStatusSchema, type RiskItemAssessmentDTO, type RiskItemDTO } from '@deepguide-ai/types'
import RiskDetailPanel from '@/pages/risk/RiskDetailPanel'
import { AcceptanceCard, AcceptRiskDialog, LinkedTasksSection } from '@/pages/risk/RiskDetailSections'
import AddRiskDialog from '@/pages/risk/AddRiskDialog'
import { Button } from '@deepguide-ai/ui/button'
import { DropdownMenuItem } from '@deepguide-ai/ui/dropdown-menu'
import { Badge } from '@deepguide-ai/ui/badge'
import { Skeleton } from '@deepguide-ai/ui/skeleton'
import { TableEmptyState } from '@deepguide-ai/ui/table-empty-state'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@deepguide-ai/ui/table'
import { ArrowLeft, AlertTriangle, Check, Eye, Loader2, Pencil, Sparkles, Trash2 } from 'lucide-react'
import { usePageHeaderTitle } from '@/lib/page-header'
import { formatDate } from '@/lib/format'
import { trpc } from '@/lib/trpc'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { DetailPanelSkeleton } from '@/components/compliance/detail-primitives'
import { DetailSection } from '@/components/compliance/detail-primitives'
import { ConfirmDialog } from '@/components/confirm-dialog'
import {
  PageHeaderBreadcrumb,
} from '@/components/layout/PageHeaderActions'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/risks_/$riskId'
)({
  validateSearch: (search: Record<string, unknown>) => ({
    tab:
      typeof search.tab === 'string' &&
      ['details', 'tests', 'tasks', 'scope', 'source', 'history'].includes(search.tab)
        ? search.tab
        : undefined,
  }),
  component: RiskItemDetailPage,
  staticData: {
    pageHeader: { title: 'Risk Detail', pad: false, scroll: false },
  },
})

function RiskItemDetailPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const deleteMutation = useMutation(trpc.riskRegister.delete.mutationOptions())
  const [acceptOpen, setAcceptOpen] = useState(false)
  const startAssessment = useMutation(trpc.riskRegister.startItemAssessment.mutationOptions())
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [metadataUpdate, setMetadataUpdate] = useState<RiskItemDTO | null>(null)
  const [assessment, setAssessment] = useState<RiskItemAssessmentDTO | null>(null)
  const openedPendingReview = useRef<string | null>(null)
  const [assessmentError, setAssessmentError] = useState<string | null>(null)
  const [residualReviewAssessmentId, setResidualReviewAssessmentId] = useState<string | null>(null)
  const { workspaceId, riskId } = Route.useParams()
  const { tab } = Route.useSearch()
  const { canWorkspaceWrite } = useWorkspacePermissions(workspaceId)

  const detailQuery = useQuery(
    trpc.riskRegister.getById.queryOptions(
      { id: riskId ?? '' },
      { enabled: Boolean(riskId), refetchInterval: query => ['OPEN', 'ASSESSING', 'IN_PROGRESS'].includes(query.state.data?.status ?? '') ? 3000 : false }
    )
  )
  const assessments = useInfiniteQuery({
    ...trpc.riskRegister.listItemAssessments.infiniteQueryOptions({ riskId }, { enabled: Boolean(detailQuery.data), refetchInterval: ['OPEN', 'ASSESSING'].includes(detailQuery.data?.status ?? '') ? 3000 : false }),
    getNextPageParam: (page) => page.nextCursor,
  })
  const activeAssessment = assessments.data?.pages[0]?.active
  const manualPendingReview = detailQuery.data?.status === 'PENDING_REVIEW' && activeAssessment && !activeAssessment.completedAt &&
    !activeAssessment.aiCompletedAt && !activeAssessment.aiProposal && !activeAssessment.confirmedAt ? activeAssessment : null
  const pendingReview = assessment ?? manualPendingReview
  const history = [...(activeAssessment ? [activeAssessment] : []), ...(assessments.data?.pages.flatMap((page) => page.history) ?? [])]
  const item = detailQuery.data ?? null
  const aiAssessmentRunning = Boolean(item?.aiAssessmentRunning || item?.status === 'ASSESSING')
  const lifecycleStatus = item?.status === 'ASSESSING' ? 'OPEN' : item?.status
  const [stageSelection, setStageSelection] = useState<{ riskId: string; status: string; stage: string } | null>(null)
  const viewStage = stageSelection?.riskId === riskId && stageSelection.status === lifecycleStatus ? stageSelection.stage : lifecycleStatus
  const aiFailed = item?.status === 'OPEN' && !aiAssessmentRunning && Boolean(activeAssessment?.aiError)

  const latestReview = history.find(record => record.completedAt && !record.supersededAt)
  const taskAssessmentId = (activeAssessment?.confirmedAt ? activeAssessment : latestReview)?.id
  const linkedTasks = useQuery(trpc.task.list.queryOptions({
    riskItemId: riskId, riskAssessmentId: taskAssessmentId, status: TaskStatusSchema.options.filter(status => status !== 'RESOLVED'), limit: 1,
  }, {
    enabled: item?.status === 'IN_PROGRESS', refetchInterval: 3000,
  }))
  const outstandingTasks = linkedTasks.data?.total ?? 0
  const treatmentReview = item?.status === 'IN_PROGRESS'
    ? activeAssessment?.confirmedAt ? activeAssessment : !activeAssessment && latestReview?.confirmedAt && !latestReview.draft.residualLikelihood && !latestReview.draft.residualImpact ? latestReview : null
    : null
  const treatmentAssessment = treatmentReview ?? (['IN_PROGRESS', 'CLOSED'].includes(item?.status ?? '') ? latestReview : null)
  const reviewingResidual = residualReviewAssessmentId === treatmentAssessment?.id && viewStage === 'IN_PROGRESS' && item?.status === 'IN_PROGRESS'
  const canReviseTreatment = Boolean((treatmentReview ?? latestReview)?.canReviseTreatment)
  const requiresAcceptance = Boolean(item?.acceptance) && ((treatmentReview ?? latestReview)?.draft.treatment === 'ACCEPT' || item?.treatment === 'ACCEPT' || item?.acceptance?.status === 'PENDING')
  const acceptanceReady = !requiresAcceptance || Boolean(item?.acceptance?.status === 'ACTIVE' && item.acceptance.approvedAt && new Date(item.acceptance.expiresAt).getTime() > Date.now())
  const followUpLoaded = linkedTasks.isSuccess && !assessments.isPending && !assessments.isError
  const canClose = !treatmentReview && followUpLoaded && !outstandingTasks && acceptanceReady && Boolean(item?.hasCompletedAssessment)
  const readyToClose = item?.status === 'IN_PROGRESS' && !item.invalidatedAt && canClose
  const editingAssessment = item?.status === 'IN_PROGRESS' && Boolean(assessment && !assessment.completedAt && !assessment.supersededAt && !assessment.confirmedAt && assessment.id !== treatmentReview?.id)

  const goBackToRegister = () => {
    navigate({
      to: '/workspaces/$workspaceId/risks',
      params: { workspaceId },
    })
  }

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: trpc.riskRegister.listItemAssessments.infiniteQueryKey({ riskId }) })
    queryClient.invalidateQueries({ queryKey: trpc.riskRegister.listActivity.queryKey() })
    queryClient.invalidateQueries({ queryKey: trpc.riskRegister.suggestResidual.queryKey() })
    queryClient.invalidateQueries({ queryKey: trpc.task.list.queryKey() })
    queryClient.invalidateQueries({ queryKey: trpc.riskRegister.getResidualMatrix.queryKey() })
    queryClient.invalidateQueries({ queryKey: trpc.riskRegister.getDistribution.queryKey() })
    queryClient.invalidateQueries({
      queryKey: trpc.riskRegister.list.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.riskRegister.getStats.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.riskRegister.getMatrix.queryKey(),
    })
    if (riskId) {
      queryClient.invalidateQueries({
        queryKey: trpc.riskRegister.getById.queryKey({ id: riskId }),
      })
    }
  }

  const handleDelete = async () => {
    if (!canWorkspaceWrite || !item) return
    await deleteMutation.mutateAsync({ id: item.id })
    invalidateAll()
    setShowDeleteDialog(false)
    goBackToRegister()
  }

  const handleAssess = async () => {
    if (!item?.canAssessRisk) return
    setAssessmentError(null)
    try {
      const record = await startAssessment.mutateAsync({ riskId: item.id, ...(item.status === 'CLOSED' ? { restart: true } : item.status === 'OPEN' ? { withAI: true } : canReviseTreatment ? { reviseTreatment: true } : {}) })
      if (!['OPEN', 'CLOSED'].includes(item.status)) {
        await detailQuery.refetch({ throwOnError: true })
        setAssessment(record)
      }
      invalidateAll()
    } catch (error) {
      setAssessmentError(error instanceof Error ? error.message : 'Failed to start assessment')
    }
  }
  useEffect(() => {
    if (item?.status !== 'PENDING_REVIEW' || !item.canAssessRisk || item.invalidatedAt || assessment || startAssessment.isPending || assessments.isPending || assessments.isError) return
    if (manualPendingReview) return
    const key = `${item.id}:${activeAssessment?.id ?? 'manual'}`
    if (openedPendingReview.current === key) return
    openedPendingReview.current = key
    void handleAssess()
  })
  usePageHeaderTitle(item ? `${item.displayId}: ${item.title}` : undefined)

  if (detailQuery.isLoading && !detailQuery.data) {
    return (
      <div className='flex h-[calc(var(--app-vh)_-_50px)] flex-col overflow-hidden'>
        <DetailPanelSkeleton />
      </div>
    )
  }

  if (!riskId || detailQuery.isError || !item) {
    return (
      <div className='flex h-[calc(var(--app-vh)_-_50px)] flex-col overflow-hidden px-6 py-6'>
        <button
          type='button'
          className='text-text-subtle hover:bg-muted hover:text-text-secondary inline-flex w-fit items-center gap-1 rounded-sm px-1.5 py-1 text-[13px] leading-4 transition-colors'
          onClick={goBackToRegister}
        >
          <ArrowLeft className='h-3.5 w-3.5' />
          Risks
        </button>
        <div className='border-border-divider rounded-sm border border-dashed bg-card px-5 py-10 text-center'>
          <AlertTriangle className='text-text-disabled mx-auto mb-3 h-8 w-8' />
          <p className='font-body text-text-body text-[13px] font-medium'>
            Risk not found
          </p>
          <p className='font-body text-muted-foreground mt-1 text-[12px]'>
            This risk may have been deleted or is no longer available.
          </p>
        </div>
      </div>
    )
  }

  const assessmentActions = (
    <div className='flex flex-wrap gap-2'>
              {!editingAssessment && ((!treatmentReview && item.status !== 'IN_PROGRESS') || canReviseTreatment) && item.canAssessRisk && ['OPEN', 'PENDING_REVIEW', 'IN_PROGRESS'].includes(lifecycleStatus ?? '') && <Button size='sm' aria-busy={aiAssessmentRunning || startAssessment.isPending} disabled={aiAssessmentRunning || startAssessment.isPending || assessments.isPending || assessments.isError} onClick={() => void handleAssess()}>
                {aiAssessmentRunning || startAssessment.isPending ? <Loader2 aria-hidden='true' className='size-4 animate-spin motion-reduce:animate-none' /> : <Sparkles />}
                {aiAssessmentRunning || startAssessment.isPending ? 'Assessing…' : canReviseTreatment ? 'Revise treatment plan' : lifecycleStatus === 'OPEN' ? 'Start assessment' : activeAssessment ? 'Continue assessment' : 'Start assessment'}
              </Button>}
              </div>
  )

  return (
    <div className='flex h-[calc(var(--app-vh)_-_50px)] flex-col overflow-hidden'>
      <PageHeaderBreadcrumb>
        <button
          type='button'
          onClick={goBackToRegister}
          className='font-heading text-text-subtle text-[15px] font-medium hover:text-cobalt-strong'
        >
          Risks
        </button>
      </PageHeaderBreadcrumb>
      <RiskDetailPanel onMetadataSaved={setMetadataUpdate}
        menuActions={canWorkspaceWrite && item.provider === 'manual' && <>
            {item.canAssessRisk && !item.invalidatedAt && <DropdownMenuItem onSelect={() => setShowEditDialog(true)} disabled={aiAssessmentRunning}>
              <Pencil className='size-4' />Edit Risk
            </DropdownMenuItem>}
            <DropdownMenuItem onSelect={() => setShowDeleteDialog(true)} disabled={deleteMutation.isPending || aiAssessmentRunning}>
              <Trash2 className='size-4' />{deleteMutation.isPending ? 'Deleting...' : 'Delete permanently…'}
            </DropdownMenuItem>
        </>}
        item={item.status === 'CLOSED' && latestReview?.snapshot ? { ...item,
          ownerId: latestReview.snapshot.draft.ownerId,
          ownerLabel: latestReview.snapshot.risk.ownerLabel,
          ownerRoleName: latestReview.snapshot.risk.ownerRoleName,
          targetDate: latestReview.snapshot.draft.targetDate,
          reviewDate: latestReview.snapshot.draft.reviewDate,
          residualLikelihood: latestReview.snapshot.draft.residualLikelihood,
          residualImpact: latestReview.snapshot.draft.residualImpact,
          residualRisk: latestReview.snapshot.risk.residualRisk,
          residualScore: latestReview.snapshot.risk.residualScore,
          acceptance: latestReview.snapshot.risk.acceptance ?? null,
        } : item}
        closedAssessment={scoreOverview =>
          <AssessRiskDialog inline metadataUpdate={metadataUpdate?.id === riskId ? metadataUpdate : undefined} hideMetadata closePreview closedScoreOverview={scoreOverview}
            key={`closed-${latestReview?.id ?? item.id}`} assessment={latestReview?.snapshot ? latestReview : undefined} item={item} onClose={() => {}} onChanged={() => {}} />}
        closedActions={item.canAssessRisk && !item.invalidatedAt && <div className='flex justify-end'>
            <Button disabled={startAssessment.isPending || aiAssessmentRunning} onClick={() => void handleAssess()}>
              {startAssessment.isPending ? 'Re-opening…' : 'Re-open'}
            </Button>
          </div>}
        viewStage={item.invalidatedAt ? undefined : viewStage}
        onClose={goBackToRegister}
        onUpdate={invalidateAll}
        readOnly={!item.canAssessRisk || aiAssessmentRunning}
        acceptanceInAssessmentPanel={item.status === 'IN_PROGRESS'}
        reviewingResidual={reviewingResidual}
        prioritizeResidualAssessment={readyToClose}
        inProgressAssessment={item.canAssessRisk && editingAssessment && assessment ? <AssessRiskDialog inline metadataUpdate={metadataUpdate?.id === riskId ? metadataUpdate : undefined} key={assessment.id} assessment={assessment} item={item}
          onClose={() => setAssessment(null)} onChanged={invalidateAll} /> : <div className='space-y-5'>
          <div hidden={reviewingResidual} className='space-y-3 empty:hidden'>
              {canReviseTreatment && <div role='status' className='rounded-sm border border-status-orange-border bg-status-orange-bg p-3 text-sm'>
                <p className='font-medium'>Risk acceptance was rejected or revoked. Revise the treatment plan or renew the existing formal acceptance.</p>
                {item.acceptance?.revokedReason && <p className='mt-1'>{item.acceptance.revokedReason}</p>}
              </div>}
              {outstandingTasks > 0 && <LinkedTasksSection riskId={item.id} riskAssessmentId={taskAssessmentId} readOnly outstandingOnly />}
              {!acceptanceReady && item.acceptance && <AcceptanceCard riskId={item.id} acceptance={item.acceptance} canDecide={item.canDecideAcceptance ?? false} canRequestApproval={item.canRequestAcceptance ?? false} onRequestApproval={() => setAcceptOpen(true)} onChanged={invalidateAll} />}
              {!acceptanceReady && item.acceptance && item.canRequestAcceptance && item.acceptance?.status !== 'PENDING' && <Button variant='outline' onClick={() => setAcceptOpen(true)}>Request acceptance</Button>}
            </div>
          <AssessRiskDialog inline metadataUpdate={metadataUpdate?.id === riskId ? metadataUpdate : undefined}
            key={`treatment-${treatmentAssessment?.id ?? item.id}`} assessment={treatmentAssessment ?? undefined} item={item}
            onResidualReview={() => setResidualReviewAssessmentId(treatmentAssessment?.id ?? null)}
            preview={!treatmentReview || viewStage !== lifecycleStatus || !item.canAssessRisk || Boolean(item.invalidatedAt)}
            completionBlocked={canReviseTreatment || !followUpLoaded || outstandingTasks > 0 || !acceptanceReady}
            onChanged={invalidateAll} onClose={() => setAssessment(null)} />
        </div>}
        assessmentPreview={<AssessRiskDialog inline metadataUpdate={metadataUpdate?.id === riskId ? metadataUpdate : undefined} disabled closePreview={viewStage !== 'PENDING_REVIEW'}
          key={`preview-${(activeAssessment ?? latestReview)?.id ?? item.id}`} assessment={activeAssessment ?? latestReview} item={item}
          onClose={() => {}} onChanged={() => {}} />}
        pendingAssessment={item.canAssessRisk ? pendingReview && !pendingReview.completedAt ? <AssessRiskDialog inline metadataUpdate={metadataUpdate?.id === riskId ? metadataUpdate : undefined} key={pendingReview.id} assessment={pendingReview} item={item}
          onClose={() => setAssessment(null)} onChanged={invalidateAll} /> : <div className='space-y-3'>
          <p className='text-sm text-muted-foreground'>{startAssessment.isPending || assessments.isPending ? 'Loading assessment…' : 'Continue the assessment to review this risk.'}</p>
          {assessmentActions}
        </div> : undefined}
        openAssessmentActions={item.canAssessRisk && <div className='space-y-3'>
          <p className='text-xs text-muted-foreground'>{aiFailed ? 'AI assessment failed. Click Start assessment to try again.' : 'Click Start assessment to generate AI suggestions and review this risk.'}</p>
          <div className='flex justify-end'>{assessmentActions}</div>
        </div>}
        tab={tab ?? 'details'}
        assessmentPanel={() => (
          <div className='mt-5 space-y-3'>
            {['OPEN', 'PENDING_REVIEW', 'IN_PROGRESS'].includes(lifecycleStatus ?? '') && <nav aria-label='Risk progress' className='px-2 py-3'>
              <ol className='flex pb-9'>
                {[
                  { status: 'OPEN', label: RISK_STATUS_LABELS.OPEN },
                  { status: 'PENDING_REVIEW', label: RISK_STATUS_LABELS.PENDING_REVIEW },
                  { status: 'IN_PROGRESS', label: RISK_STATUS_LABELS.IN_PROGRESS },
                  { status: 'CLOSED', label: RISK_STATUS_LABELS.CLOSED },
                ].map((stage, index, stages) => {
                  const current = stage.status === lifecycleStatus
                  const reached = index <= stages.findIndex(value => value.status === lifecycleStatus)
                  const completed = reached && (!current || stage.status === 'CLOSED')
                  return <li key={stage.status} aria-current={current ? 'step' : undefined} className='relative flex min-w-0 flex-1 flex-col items-start text-[11px] last:flex-none'>
                    {index < stages.length - 1 && <span aria-hidden='true' className={`absolute top-3.5 left-7 w-[calc(100%-1.75rem)] border-t ${completed ? 'border-status-green' : 'border-border'}`} />}
                    <button type='button' disabled={!reached} aria-label={`View ${stage.label}`} aria-pressed={viewStage === stage.status}
                      onClick={() => setStageSelection({ riskId, status: lifecycleStatus!, stage: stage.status })}
                      className='relative flex flex-col items-start disabled:cursor-not-allowed'>
                    <span aria-hidden='true' className={`relative flex size-7 items-center justify-center rounded-full border ring-4 ring-card ${
                      viewStage === stage.status ? 'border-ring bg-selected text-cobalt-strong' : completed ? 'border-status-green bg-status-green-bg text-status-green-strong' : current ? 'border-status-orange bg-card' : 'border-dashed border-text-placeholder bg-card'
                    }`}>
                      {completed ? <Check className='size-3.5' /> : current ? <span className={`size-2.5 rounded-full ${viewStage === stage.status ? 'bg-ring' : 'bg-status-orange'}`} /> : null}
                    </span>
                    <span className={`absolute top-9 left-3.5 w-24 -translate-x-1/2 text-center max-sm:w-16 ${viewStage === stage.status ? 'font-semibold text-cobalt-strong' : completed ? 'text-status-green-strong' : current ? 'font-semibold text-status-orange-strong' : 'text-muted-foreground'}`}>{stage.label}</span>
                    </button>
                  </li>
                })}
              </ol>
            </nav>}

            <div hidden={viewStage !== lifecycleStatus} className='space-y-3'>

            {!treatmentReview && !['OPEN', 'PENDING_REVIEW', 'CLOSED'].includes(lifecycleStatus ?? '') && (aiFailed || item.hasCompletedAssessment != null) && (
              <p className='text-xs text-muted-foreground'>
                {aiFailed ? 'AI assessment failed. Click Start assessment to review this risk manually.' : item.hasCompletedAssessment ? 'Review the residual risk below and decide whether to close this risk.' : 'Initial estimate · not assessed'}
              </p>
            )}

            {item.status === 'IN_PROGRESS' && assessmentActions}
            {!['OPEN', 'PENDING_REVIEW', 'IN_PROGRESS', 'CLOSED'].includes(lifecycleStatus ?? '') && <div className='flex flex-wrap items-center justify-between gap-3 rounded-md bg-cobalt-tonal px-4 py-3'>
              <div className='space-y-1'>
                <p className='text-sm font-medium'>
                  {{
                    MONITORING: 'Risk monitoring',
                  }[item.status]}
                </p>
                <p className='text-sm text-muted-foreground'>
                  {item.aiAssessmentState === 'unknown' ? 'Unable to verify the AI task. Please refresh and try again.' : item.aiAssessmentState === 'queued' ? 'An AI assessment is queued. Manual review will be available after it finishes.' : activeAssessment ? activeAssessment.aiCompletedAt ? 'AI suggestions ready for human review' : activeAssessment.aiError || 'Waiting for the background AI assessment'
                    : null}
                </p>
              </div>
              {assessmentActions}
            </div>}
            {assessmentError && <p role='alert' className='text-sm text-destructive'>{assessmentError}</p>}
            </div>
            {assessments.isError && <p role='alert' className='text-sm text-destructive'>Assessment history could not be loaded. <Button variant='link' onClick={() => void assessments.refetch()}>Retry</Button></p>}
          </div>
        )}
        assessmentHistory={
          <DetailSection title='Assessment History'>
            {assessments.isPending ? (
              <Skeleton className='h-9 w-full rounded-sm' />
            ) : history.length === 0 ? (
              !assessments.isError && <TableEmptyState className='min-h-0 px-4 py-4'>
                <p className='font-body text-text-placeholder text-[12px]'>Assessments will appear here after you start one.</p>
              </TableEmptyState>
            ) : (
              <div className='border-border bg-card overflow-hidden rounded-md border'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className='px-3'>Status</TableHead>
                      <TableHead className='px-3'>Started</TableHead>
                      <TableHead className='px-3'>Completed</TableHead>
                      <TableHead className='px-3'>Completed by</TableHead>
                      <TableHead className='px-3'>Treatment</TableHead>
                      <TableHead className='px-3 text-right'>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell className='font-body text-text-body px-3 py-2 text-[11px]'>
                          <Badge status={record.supersededAt ? 'default' : record.completedAt ? 'green' : 'blue'}>{record.supersededAt ? 'Superseded' : record.id === treatmentReview?.id ? 'Treatment in progress' : record.completedAt ? 'Completed' : record.confirmedAt ? 'Treatment in progress' : record.id === activeAssessment?.id && ['queued', 'running'].includes(item.aiAssessmentState ?? '') ? 'AI assessment' : 'Awaiting review'}</Badge>
                        </TableCell>
                        <TableCell className='font-body text-muted-foreground px-3 py-2 text-[11px] whitespace-nowrap'>{formatDate(record.startedAt)}</TableCell>
                        <TableCell className='font-body text-muted-foreground px-3 py-2 text-[11px] whitespace-nowrap'>{record.completedAt && record.id !== treatmentReview?.id ? formatDate(record.completedAt) : '—'}</TableCell>
                        <TableCell className='font-body text-text-body px-3 py-2 text-[11px]'>{record.completedAt && record.id !== treatmentReview?.id ? record.snapshot?.completedByName ?? record.completedByUserId : '—'}</TableCell>
                        <TableCell className='font-body text-text-body px-3 py-2 text-[11px]'>{record.snapshot?.draft.treatment ?? record.draft.treatment}</TableCell>
                        <TableCell className='px-3 py-2 text-right'>
                          {(record.completedAt || record.supersededAt) && record.id !== treatmentReview?.id &&
                          <Button
                            variant='ghost'
                            size='xxsIcon'
                            className='text-text-subtle hover:bg-muted hover:text-text-secondary'
                            aria-label='View result'
                            tooltip='View result'
                            onClick={() => { setAssessment(record) }}
                          >
                            <Eye className='size-4' aria-hidden='true' />
                          </Button>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {assessments.hasNextPage && <Button variant='outline' size='sm' disabled={assessments.isFetchingNextPage} onClick={() => void assessments.fetchNextPage()}>Load older assessments</Button>}
          </DetailSection>
        }
        onTabChange={(next) =>
          navigate({
            to: '/workspaces/$workspaceId/risks/$riskId',
            params: { workspaceId, riskId },
            search: { tab: next },
            replace: true,
          })
        }
      />
      {acceptOpen && <AcceptRiskDialog open riskId={item.id} initialAcceptance={item.acceptance} onOpenChange={setAcceptOpen} onAccepted={() => { setAcceptOpen(false); invalidateAll() }} />}
      {showEditDialog && <AddRiskDialog key={item.id} open item={item} onClose={() => setShowEditDialog(false)} onCreated={invalidateAll} />}
      {assessment && (assessment.completedAt || assessment.supersededAt) && (
        !assessment.snapshot || (assessment.confirmedAt && assessment.completedAt && !assessment.supersededAt && !assessment.draft.residualLikelihood && !assessment.draft.residualImpact)
          ? <AssessRiskDialog key={assessment.id} assessment={assessment} item={item} onClose={() => setAssessment(null)} onChanged={invalidateAll} />
          : <RiskAssessmentDocumentDialog key={assessment.id} assessment={assessment} onClose={() => setAssessment(null)} onChanged={invalidateAll} />
      )}
      {assessment && !assessment.completedAt && !assessment.supersededAt && !editingAssessment && !treatmentReview && item.status !== 'PENDING_REVIEW' && <AssessRiskDialog key={assessment.id} assessment={assessment} item={item}
        onClose={() => setAssessment(null)} onChanged={invalidateAll} />}
      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title='Delete risk permanently?'
        desc={`Are you sure you want to delete "${item.displayId}"? This action cannot be undone.`}
        destructive
        confirmText={deleteMutation.isPending ? 'Deleting...' : 'Delete permanently'}
        isLoading={deleteMutation.isPending}
        handleConfirm={() => {
          void handleDelete()
        }}
      />
    </div>
  )
}
